import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { ArrowLeft, Save, Printer, Loader2, Award, Download, Mail } from 'lucide-react';
import SendEmailModal from '../../components/SendEmailModal';
import { generateDocPdf } from '../../utils/generateDocPdf';
import { useToast } from '../../hooks/use-toast';
import FreightCertificatePrint from './FreightCertificatePrint';

const FreightCertificateForm = () => {
  const { docketId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const printRef = useRef(null);
  const hiddenPrintRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [documentId, setDocumentId] = useState(null);
  const [docketInfo, setDocketInfo] = useState(null);
  const [companyData, setCompanyData] = useState(null);
  const [showPrintView, setShowPrintView] = useState(false);

  const [formData, setFormData] = useState({
    job_no: '',
    date: '',
    shipper: '',
    consignee: '',
    airline: '',
    flight_no: '',
    flight_date: '',
    airport_of_load: '',
    airport_of_discharge: '',
    temperature: '',
    mawb_no: '',
    mawb_date: '',
    mawb_packages: '',
    mawb_gross_weight: '',
    mawb_description: '',
    hawb_no: '',
    hawb_date: '',
    hawb_packages: '',
    hawb_gross_weight: '',
    hawb_description: '',
    description: '',
    quantity: '',
    currency: 'INR',
    amount: '',
    total_amount: ''
  });

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const getAuthHeaders = () => {
    const token = localStorage.getItem('auth_token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const docketResponse = await fetch(`${backendUrl}/api/dockets/${docketId}/print-data`, {
          headers: getAuthHeaders()
        });
        if (!docketResponse.ok) throw new Error('Failed to fetch docket data');
        const docketData = await docketResponse.json();
        setDocketInfo(docketData);
        if (docketData.company) setCompanyData(docketData.company);

        // Check if Freight Certificate already exists
        const fcResponse = await fetch(
          `${backendUrl}/api/dockets/${docketId}/documents/FREIGHT_CERTIFICATE`,
          { headers: getAuthHeaders() }
        );

        if (fcResponse.ok) {
          const fcData = await fcResponse.json();
          if (fcData && fcData.id) {
            const savedData = { ...(fcData.document_data || {}) };
            if (!savedData.airline) {
              savedData.airline = docketData?.resolved_shipment?.carrier || docketData?.docket?.payload_json?.shipment_info?.carrier || docketData?.docket?.payload_json?.shipment_info?.airline || '';
            }
            setFormData(savedData);
            setDocumentId(fcData.id);
            setIsSaved(true);
          } else {
            populateFromDocket(docketData);
          }
        } else {
          populateFromDocket(docketData);
        }
      } catch (error) {
        toast({ title: 'Error', description: error.message, variant: 'destructive' });
      } finally {
        setLoading(false);
      }
    };

    if (docketId) fetchData();
  }, [docketId, backendUrl, toast]);

  const populateFromDocket = (docketData) => {
    const { docket, resolved_parties, resolved_shipment } = docketData;
    const payload = docket.payload_json || {};
    const header = payload.header || {};
    const shipmentInfo = payload.shipment_info || {};
    const invoices = payload.invoices || [];
    const invoice = invoices[0] || {};

    const buildPartyText = (party) => {
      if (!party) return '';
      return [
        party.name,
        party.branch_name,
        party.address,
        party.phone ? `Tel: ${party.phone}` : '',
        party.email ? `Email: ${party.email}` : ''
      ].filter(Boolean).join('\n');
    };

    setFormData({
      job_no: docket.system_docket_no || '',
      date: new Date().toISOString().split('T')[0],
      shipper: buildPartyText(resolved_parties?.exporter_shipper),
      consignee: buildPartyText(resolved_parties?.importer_receiver),
      airline: resolved_shipment?.carrier || shipmentInfo.carrier || shipmentInfo.airline || '',
      flight_no: shipmentInfo.flight_no || '',
      flight_date: shipmentInfo.flight_date || '',
      airport_of_load: resolved_shipment?.loading_from || '',
      airport_of_discharge: resolved_shipment?.discharge_at || '',
      temperature: header.temp || header.temperature || '',
      mawb_no: shipmentInfo.mawb_no || '',
      mawb_date: shipmentInfo.mawb_date || '',
      mawb_packages: invoice.no_of_packages || '',
      mawb_gross_weight: invoice.gross_weight || '',
      mawb_description: invoice.description || shipmentInfo.hawb_description || '',
      hawb_no: shipmentInfo.hawb_no || '',
      hawb_date: shipmentInfo.hawb_date || '',
      hawb_packages: invoice.no_of_packages || '',
      hawb_gross_weight: invoice.gross_weight || '',
      hawb_description: invoice.description || shipmentInfo.hawb_description || '',
      description: invoice.description || '',
      quantity: invoice.no_of_packages || '',
      currency: 'INR',
      amount: '',
      total_amount: ''
    });
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      const url = isSaved
        ? `${backendUrl}/api/dockets/${docketId}/documents/FREIGHT_CERTIFICATE`
        : `${backendUrl}/api/dockets/${docketId}/documents`;

      const method = isSaved ? 'PUT' : 'POST';

      const body = isSaved
        ? { document_data: formData }
        : { document_type: 'FREIGHT_CERTIFICATE', document_data: formData };

      const response = await fetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to save');
      }

      const savedData = await response.json();
      setDocumentId(savedData.id);
      setIsSaved(true);

      toast({ title: 'Success', description: 'Freight Certificate saved successfully' });
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handlePrint = () => {
    const originalTitle = document.title;
    document.title = `FC_${formData.job_no || docketId}`;
    setShowPrintView(true);
    setTimeout(() => {
      window.print();
      setTimeout(() => { document.title = originalTitle; }, 1000);
    }, 300);
  };

  const handleSavePDF = () => {
    const originalTitle = document.title;
    document.title = `FC_${formData.job_no || docketId}`;
    setShowPrintView(true);
    setTimeout(() => {
      window.print();
      setTimeout(() => { document.title = originalTitle; }, 1000);
    }, 300);
  };

  const pdfFetcher = async () => {
    if (!hiddenPrintRef.current) return null;
    const el = hiddenPrintRef.current;
    el.style.left = '0';
    await new Promise(r => setTimeout(r, 1500));
    try {
      return await generateDocPdf(el);
    } finally {
      el.style.left = '-9999px';
    }
  };

  // Print view
  if (showPrintView) {
    document.title = `FC_${formData.job_no || docketId}`;
    return (
      <div className="min-h-screen bg-white">
        <div className="print:hidden fixed top-4 left-4 right-4 z-50 flex justify-between">
          <Button
            variant="outline"
            onClick={() => { document.title = 'LogisticsHub'; setShowPrintView(false); }}
            className="bg-white shadow-md"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Form
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => window.print()} className="bg-white shadow-md">
              <Download className="w-4 h-4 mr-2" />
              Save as PDF
            </Button>
            <Button onClick={() => window.print()} className="bg-amber-600 hover:bg-amber-700 shadow-md">
              <Printer className="w-4 h-4 mr-2" />
              Print
            </Button>
            <Button variant="outline" onClick={() => setShowEmailModal(true)}
              className="border-orange-500 text-orange-600 hover:bg-orange-50 bg-white shadow-md" data-testid="fc-send-email-btn">
              <Mail className="w-4 h-4 mr-2" /> Send Mail
            </Button>
          </div>
        </div>
        <div className="pt-16">
          <FreightCertificatePrint
            ref={printRef}
            docketId={docketId}
            formData={formData}
            companyData={companyData}
          />
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-accent-blue" />
      </div>
    );
  }

  return (
    <>
    <div className="min-h-screen bg-surface-light p-6">
      <div className="max-w-4xl mx-auto space-y-6">

        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-xl font-bold text-text-dark font-manrope flex items-center gap-2">
                <Award className="w-5 h-5 text-amber-600" />
                Freight Certificate
              </h1>
              <p className="text-sm text-text-secondary font-ibm">
                Docket: {docketInfo?.docket?.system_docket_no || docketId}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isSaved && (
              <>
                <Button variant="outline" onClick={handleSavePDF} data-testid="fc-save-pdf-btn">
                  <Download className="w-4 h-4 mr-2" />
                  Save PDF
                </Button>
                <Button variant="outline" onClick={handlePrint} data-testid="fc-print-btn">
                  <Printer className="w-4 h-4 mr-2" />
                  Print
                </Button>
              </>
            )}
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="fc-save-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>

        {/* Form */}
        <Card className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="bg-gradient-to-r from-amber-600 to-amber-500 px-5 py-3">
            <h2 className="text-base font-semibold text-white font-ibm">FREIGHT CERTIFICATE</h2>
          </div>

          <div className="p-5 space-y-5">

            {/* Row 1: Job No, Date */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-semibold text-text-secondary font-ibm">Job No.</Label>
                <Input
                  value={formData.job_no}
                  readOnly
                  className="mt-1 bg-gray-50 cursor-not-allowed"
                  data-testid="fc-job-no"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold text-text-secondary font-ibm">Date</Label>
                <Input
                  type="date"
                  value={formData.date}
                  onChange={(e) => handleChange('date', e.target.value)}
                  className="mt-1"
                  data-testid="fc-date"
                />
              </div>
            </div>

            {/* Row 2: Shipper, Consignee */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-semibold text-text-secondary font-ibm">Shipper <span className="text-red-500">*</span></Label>
                <Textarea
                  value={formData.shipper}
                  onChange={(e) => handleChange('shipper', e.target.value)}
                  className="mt-1 h-28"
                  placeholder="Shipper name and address"
                  data-testid="fc-shipper"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold text-text-secondary font-ibm">Consignee <span className="text-red-500">*</span></Label>
                <Textarea
                  value={formData.consignee}
                  onChange={(e) => handleChange('consignee', e.target.value)}
                  className="mt-1 h-28"
                  placeholder="Consignee name and address"
                  data-testid="fc-consignee"
                />
              </div>
            </div>

            {/* Row 3: Airline, Flight No, Flight Date, Airport of Load, Airport of Discharge, Temperature */}
            <div className="grid grid-cols-6 gap-3">
              <div>
                <Label className="text-xs font-semibold text-text-secondary font-ibm">Airline</Label>
                <Input
                  value={formData.airline}
                  onChange={(e) => handleChange('airline', e.target.value)}
                  className="mt-1"
                  data-testid="fc-airline"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold text-text-secondary font-ibm">Flight No.</Label>
                <Input
                  value={formData.flight_no}
                  onChange={(e) => handleChange('flight_no', e.target.value)}
                  className="mt-1"
                  data-testid="fc-flight-no"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold text-text-secondary font-ibm">Flight Date</Label>
                <Input
                  type="date"
                  value={formData.flight_date}
                  onChange={(e) => handleChange('flight_date', e.target.value)}
                  className="mt-1"
                  data-testid="fc-flight-date"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold text-text-secondary font-ibm">Airport of Load</Label>
                <Input
                  value={formData.airport_of_load}
                  onChange={(e) => handleChange('airport_of_load', e.target.value)}
                  className="mt-1"
                  data-testid="fc-airport-load"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold text-text-secondary font-ibm">Airport of Discharge</Label>
                <Input
                  value={formData.airport_of_discharge}
                  onChange={(e) => handleChange('airport_of_discharge', e.target.value)}
                  className="mt-1"
                  data-testid="fc-airport-discharge"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold text-text-secondary font-ibm">Temperature</Label>
                <Input
                  value={formData.temperature}
                  onChange={(e) => handleChange('temperature', e.target.value)}
                  className="mt-1"
                  placeholder="e.g. 2-8°C"
                  data-testid="fc-temperature"
                />
              </div>
            </div>

            {/* MAWB Section */}
            <div className="border-t border-gray-200 pt-4">
              <p className="text-xs font-bold text-text-secondary uppercase tracking-wide font-ibm mb-3">MAWB Details</p>
              <div className="grid grid-cols-5 gap-3">
                <div>
                  <Label className="text-xs font-semibold text-text-secondary font-ibm">MAWB No.</Label>
                  <Input
                    value={formData.mawb_no}
                    onChange={(e) => handleChange('mawb_no', e.target.value)}
                    className="mt-1"
                    data-testid="fc-mawb-no"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-text-secondary font-ibm">MAWB Date</Label>
                  <Input
                    type="date"
                    value={formData.mawb_date}
                    onChange={(e) => handleChange('mawb_date', e.target.value)}
                    className="mt-1"
                    data-testid="fc-mawb-date"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-text-secondary font-ibm">No. of Packages</Label>
                  <Input
                    value={formData.mawb_packages}
                    onChange={(e) => handleChange('mawb_packages', e.target.value)}
                    className="mt-1"
                    data-testid="fc-mawb-packages"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-text-secondary font-ibm">Gross Weight</Label>
                  <Input
                    value={formData.mawb_gross_weight}
                    onChange={(e) => handleChange('mawb_gross_weight', e.target.value)}
                    className="mt-1"
                    data-testid="fc-mawb-gross-weight"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-text-secondary font-ibm">Description MAWB</Label>
                  <Textarea
                    value={formData.mawb_description}
                    onChange={(e) => handleChange('mawb_description', e.target.value)}
                    className="mt-1 h-20"
                    data-testid="fc-mawb-description"
                  />
                </div>
              </div>
            </div>

            {/* HAWB Section */}
            <div className="border-t border-gray-200 pt-4">
              <p className="text-xs font-bold text-text-secondary uppercase tracking-wide font-ibm mb-3">HAWB Details</p>
              <div className="grid grid-cols-5 gap-3">
                <div>
                  <Label className="text-xs font-semibold text-text-secondary font-ibm">HAWB No.</Label>
                  <Input
                    value={formData.hawb_no}
                    onChange={(e) => handleChange('hawb_no', e.target.value)}
                    className="mt-1"
                    data-testid="fc-hawb-no"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-text-secondary font-ibm">HAWB Date</Label>
                  <Input
                    type="date"
                    value={formData.hawb_date}
                    onChange={(e) => handleChange('hawb_date', e.target.value)}
                    className="mt-1"
                    data-testid="fc-hawb-date"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-text-secondary font-ibm">No. of Packages</Label>
                  <Input
                    value={formData.hawb_packages}
                    onChange={(e) => handleChange('hawb_packages', e.target.value)}
                    className="mt-1"
                    data-testid="fc-hawb-packages"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-text-secondary font-ibm">Gross Weight</Label>
                  <Input
                    value={formData.hawb_gross_weight}
                    onChange={(e) => handleChange('hawb_gross_weight', e.target.value)}
                    className="mt-1"
                    data-testid="fc-hawb-gross-weight"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-text-secondary font-ibm">Description HAWB</Label>
                  <Textarea
                    value={formData.hawb_description}
                    onChange={(e) => handleChange('hawb_description', e.target.value)}
                    className="mt-1 h-20"
                    data-testid="fc-hawb-description"
                  />
                </div>
              </div>
            </div>

            {/* Freight Certificate Details */}
            <div className="border-t border-gray-200 pt-4">
              <p className="text-xs font-bold text-text-secondary uppercase tracking-wide font-ibm mb-3">Freight Certificate Details</p>
              <div className="grid grid-cols-5 gap-3">
                <div>
                  <Label className="text-xs font-semibold text-text-secondary font-ibm">Description</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => handleChange('description', e.target.value)}
                    className="mt-1 h-20"
                    data-testid="fc-description"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-text-secondary font-ibm">Quantity</Label>
                  <Input
                    value={formData.quantity}
                    onChange={(e) => handleChange('quantity', e.target.value)}
                    className="mt-1"
                    data-testid="fc-quantity"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-text-secondary font-ibm">Currency</Label>
                  <Input
                    value={formData.currency}
                    onChange={(e) => handleChange('currency', e.target.value)}
                    className="mt-1"
                    placeholder="INR"
                    data-testid="fc-currency"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-text-secondary font-ibm">Amount</Label>
                  <Input
                    value={formData.amount}
                    onChange={(e) => handleChange('amount', e.target.value)}
                    className="mt-1"
                    data-testid="fc-amount"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-text-secondary font-ibm">Total Amount</Label>
                  <Input
                    value={formData.total_amount}
                    onChange={(e) => handleChange('total_amount', e.target.value)}
                    className="mt-1"
                    data-testid="fc-total-amount"
                  />
                </div>
              </div>
            </div>

          </div>
        </Card>

        {/* Saved status */}
        {isSaved && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center justify-between">
            <p className="text-sm text-amber-700 font-ibm">
              <span className="font-semibold">Saved</span> — Freight Certificate linked to docket {docketInfo?.docket?.system_docket_no}
            </p>
            <span className="text-xs text-amber-600">Document ID: {documentId?.toString().slice(0, 8)}...</span>
          </div>
        )}

      </div>
    </div>

    {/* Hidden print container for PDF generation */}
    {isSaved && (
      <div
        ref={hiddenPrintRef}
        style={{ position: 'absolute', left: '-9999px', top: 0, width: '794px', background: '#fff', zIndex: -1 }}
      >
        <FreightCertificatePrint docketId={docketId} formData={formData} companyData={companyData} />
      </div>
    )}

    <SendEmailModal
      isOpen={showEmailModal}
      onClose={() => setShowEmailModal(false)}
      templateKey="freight_certificate"
      variables={{
        docket_no: docketInfo?.docket?.system_docket_no || '',
        consignee_name: (formData.consignee || '').split('\n')[0],
        description: formData.description || '',
      }}
      defaultToEmail={docketInfo?.resolved_parties?.importer_receiver?.email || ''}
      defaultSubject={`Freight Certificate — ${docketInfo?.docket?.system_docket_no || ''}`}
      pdfFetcher={isSaved ? pdfFetcher : null}
      pdfFilename={`FC_${docketInfo?.docket?.system_docket_no || docketId}.pdf`}
      documentTitle={`Freight Certificate — ${docketInfo?.docket?.system_docket_no || ''}`}
      docketId={docketId}
    />
    </>
  );
};

export default FreightCertificateForm;
